/**
 * GIWAHS — Founder Dashboard — Step 10
 * ---------------------------------------------------------------------
 * Authenticated. Shows the caller's organisation + membership snapshot,
 * lifetime savings widget, 4-gate program status, Customer Portal link,
 * and the founder→standard downgrade confirmation flow.
 *
 * No mutations happen here except via membershipsClient → backend RPCs.
 *
 * State machine:
 *   - UI flag OFF                -> "Coming soon" placeholder
 *   - Backend router OFF (404)   -> "Coming soon" placeholder
 *   - Authenticated user, no membership snapshot
 *      -> "You don't have a membership yet" + CTA to /membership
 *   - Authenticated user with snapshot
 *      -> Snapshot · Savings · Gates · Portal · Danger zone
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  membershipsClient,
  isMembershipsUiEnabled,
  TIER_META,
  FOUNDER_TIERS,
  STANDARD_TIERS,
  fmtUSD,
} from '../lib/membershipsClient';

const GOLD = '#9A7B2A';

const Pill = ({ ok, label, danger }) => (
  <span
    data-testid={`gate-pill-${label.replace(/\s+/g, '-').toLowerCase()}`}
    className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${
      ok ? 'border-neutral-900 bg-neutral-900 text-white'
         : danger ? 'border-amber-900 text-amber-900 bg-amber-50'
                  : 'border-neutral-300 text-neutral-500 bg-white'
    }`}
  >
    <span>{ok ? '●' : '○'}</span> {label}
  </span>
);

const Card = ({ title, children, action, testid }) => (
  <div data-testid={testid} className="border border-neutral-200 bg-white">
    <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-5 py-4">
      <div className="label-overline">{title}</div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const SavingsWidget = ({ savingsCents }) => {
  const usd = (savingsCents || 0) / 100;
  return (
    <div data-testid="founder-savings-widget" className="border border-neutral-900 bg-neutral-900 text-white">
      <div className="px-5 py-4 border-b border-neutral-700">
        <div className="font-mono text-[10px] uppercase tracking-wider text-neutral-300">/ Lifetime savings</div>
      </div>
      <div className="p-5">
        <div className="font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '3rem', lineHeight: 0.95 }}>
          US${usd.toLocaleString('en-US', { maximumFractionDigits: 0 })}
        </div>
        <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-neutral-300">
          Founder price vs standard · accumulated
        </div>
      </div>
    </div>
  );
};

const ComingSoon = ({ reason }) => (
  <section data-testid="founder-coming-soon" className="mx-auto max-w-3xl px-5 py-24 text-center md:py-32">
    <div className="label-overline" style={{ color: GOLD }}>/ Founder dashboard · pre-launch</div>
    <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 0.95 }}>
      Your dashboard will be here.
    </h1>
    <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
      Founder activations open once the Founding Partner programme launches. You'll get an email the moment your Founding Partnership is reserved.
    </p>
    <p className="mt-2 text-[11px] uppercase tracking-wider text-neutral-400 font-mono">Mode: {reason || 'pre-launch'}</p>
    <Link to="/membership" data-testid="founder-coming-soon-cta" className="mt-8 inline-block px-6 py-3 text-sm uppercase tracking-wider font-mono bg-neutral-900 text-white">
      View the programme →
    </Link>
  </section>
);

const FounderDashboard = () => {
  const { user, loading } = useAuth();
  const navigate          = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const [busy, setBusy]   = useState(null);
  const [downgrade, setDowngrade] = useState({ open: false, target: '', typed: '' });

  useEffect(() => {
    if (!isMembershipsUiEnabled()) {
      setState({ status: 'disabled', reason: 'ui_flag_off' });
      return;
    }
    if (loading) return;
    if (!user) {
      navigate(`/login?next=${encodeURIComponent('/founder/dashboard')}`);
      return;
    }
    let cancelled = false;
    (async () => {
      const r = await membershipsClient.me();
      if (cancelled) return;
      if (r?.disabled) {
        setState({ status: 'disabled', reason: r.reason });
      } else if (r?.code === 'unauthenticated') {
        navigate(`/login?next=${encodeURIComponent('/founder/dashboard')}`);
      } else if (r?.ok) {
        setState({ status: 'ready', snapshot: r });
      } else {
        setState({ status: 'error', error: r?.detail || 'Could not load membership.' });
      }
    })();
    return () => { cancelled = true; };
  }, [user, loading, navigate]);

  const snapshot   = state.snapshot || {};
  const membership = snapshot.membership || null;
  const org        = snapshot.organisation || null;
  const meta       = membership ? TIER_META[membership.tier] : null;
  const isFounder  = membership && FOUNDER_TIERS.includes(membership.tier);

  const onPortal = async () => {
    setBusy('portal');
    try {
      const r = await membershipsClient.createPortalSession({
        return_url: `${window.location.origin}/founder/dashboard?from=portal`,
      });
      if (r?.ok && r.portal_url) window.location.href = r.portal_url;
      else toast.error(r?.detail || 'Could not open Stripe portal.');
    } finally { setBusy(null); }
  };

  const onMarkOnboarding = async () => {
    if (!membership?.id) return;
    setBusy('onboarding');
    try {
      const r = await membershipsClient.markOnboardingComplete({ membership_id: membership.id });
      if (r?.ok) { toast.success('Onboarding marked complete.'); window.location.reload(); }
      else toast.error(r?.detail || 'Could not update onboarding state.');
    } finally { setBusy(null); }
  };

  const onMarkVerification = async () => {
    if (!membership?.id) return;
    setBusy('verification');
    try {
      const r = await membershipsClient.markVerificationSubmitted({ membership_id: membership.id });
      if (r?.ok) { toast.success('Verification submission recorded.'); window.location.reload(); }
      else toast.error(r?.detail || 'Could not update verification state.');
    } finally { setBusy(null); }
  };

  const onConfirmDowngrade = async () => {
    if (!downgrade.target || downgrade.typed.trim().toUpperCase() !== 'DOWNGRADE') {
      toast.error('Type DOWNGRADE to confirm.');
      return;
    }
    setBusy('downgrade');
    try {
      const r = await membershipsClient.scheduleDowngrade({
        target_tier: downgrade.target,
        confirm:     'DOWNGRADE',
      });
      if (r?.ok) {
        toast.success('Downgrade scheduled at next renewal. Cancellable any time before then.');
        setDowngrade({ open: false, target: '', typed: '' });
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast.error(r?.detail || 'Could not schedule downgrade.');
      }
    } finally { setBusy(null); }
  };

  const onCancelDowngrade = async () => {
    setBusy('cancel-downgrade');
    try {
      const r = await membershipsClient.cancelDowngrade();
      if (r?.ok) { toast.success('Scheduled downgrade cancelled.'); window.location.reload(); }
      else toast.error(r?.detail || 'Could not cancel.');
    } finally { setBusy(null); }
  };

  // Render guards
  if (state.status === 'loading' || loading) {
    return (
      <div data-testid="founder-loading" className="mx-auto max-w-5xl px-5 py-32 text-center">
        <div className="label-overline text-neutral-500">Loading your dashboard…</div>
        <div className="mt-3 h-px w-24 mx-auto bg-neutral-300 animate-pulse" />
      </div>
    );
  }
  if (state.status === 'disabled') return <ComingSoon reason={state.reason} />;
  if (state.status === 'error') {
    return (
      <div data-testid="founder-error" className="mx-auto max-w-3xl px-5 py-24 text-center">
        <div className="label-overline" style={{ color: GOLD }}>/ Dashboard · error</div>
        <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3rem)', lineHeight: 0.95 }}>Something went wrong.</h1>
        <p className="mt-4 text-neutral-700">{state.error}</p>
      </div>
    );
  }

  if (!membership) {
    return (
      <div data-testid="founder-no-membership" className="mx-auto max-w-3xl px-5 py-24 text-center">
        <div className="label-overline" style={{ color: GOLD }}>/ No membership yet</div>
        <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2rem,5vw,3.5rem)', lineHeight: 0.95 }}>
          You're not a Founding Partner yet.
        </h1>
        <p className="mt-6 text-neutral-700 text-lg leading-relaxed">
          Choose a founder tier to lock your Founder pricing for 10 years from activation and secure a permanent founder number.
        </p>
        <Link to="/membership" data-testid="founder-no-membership-cta" className="mt-8 inline-block px-6 py-3 text-sm uppercase tracking-wider font-mono bg-neutral-900 text-white">
          View the programme →
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Founder Dashboard · GIWAHS</title></Helmet>

      <section className="mx-auto max-w-6xl px-5 py-10" data-testid="founder-dashboard">
        <div className="label-overline" style={isFounder ? { color: GOLD } : {}}>
          / {isFounder ? 'Founding Partner Dashboard' : 'Member Dashboard'}
        </div>
        <h1 className="mt-3 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem,5vw,3.5rem)', lineHeight: 0.95 }}>
          {org?.name || 'Your organisation'}
        </h1>
        <div className="mt-2 font-mono text-[11px] uppercase tracking-wider text-neutral-500">
          {meta?.label || membership.tier} · {membership.status}
          {isFounder && membership.founder_member_number ? ` · Founder #${String(membership.founder_member_number).padStart(4, '0')}` : ''}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          {/* Snapshot */}
          <Card title="/ Membership" testid="founder-card-snapshot">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2 text-sm">
              <dt className="text-neutral-500">Tier</dt><dd className="text-neutral-900">{meta?.label || membership.tier}</dd>
              <dt className="text-neutral-500">Status</dt><dd className="text-neutral-900">{membership.status}</dd>
              <dt className="text-neutral-500">Renews</dt><dd className="text-neutral-900">{(membership.current_period_end || '').slice(0, 10) || '—'}</dd>
              <dt className="text-neutral-500">Founder price</dt><dd className="text-neutral-900">{isFounder ? fmtUSD(meta?.founder_price_usd) : '—'}</dd>
              <dt className="text-neutral-500">Standard price</dt><dd className="text-neutral-900 line-through">{isFounder ? fmtUSD(meta?.standard_price_usd) : '—'}</dd>
              <dt className="text-neutral-500">Protection until</dt><dd className="text-neutral-900">{(membership.price_protection_expiry || '').slice(0, 10) || (isFounder ? '10 years from activation' : '—')}</dd>
            </dl>
          </Card>

          {/* Savings */}
          <SavingsWidget savingsCents={snapshot.lifetime_savings_cents} />

          {/* Program status — 4 gates */}
          <Card title="/ Program status" testid="founder-card-gates">
            <div className="flex flex-wrap gap-2">
              <Pill ok={!!membership.activated_at} label="Paid · Activated" />
              <Pill ok={membership.status === 'active'} label="Subscription active" danger={membership.status !== 'active'} />
              <Pill ok={!!membership.onboarding_complete_at} label="Onboarding" />
              <Pill ok={!!membership.verification_submitted_at} label="Verification submitted" />
            </div>
            {isFounder && !membership.founder_member_number && (
              <div className="mt-4 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Your founder number is held until the four gates are all green.
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              {!membership.onboarding_complete_at && (
                <button type="button" data-testid="founder-mark-onboarding-btn" disabled={busy === 'onboarding'} onClick={onMarkOnboarding}
                        className="px-3 py-2 text-xs uppercase tracking-wider font-mono border border-neutral-900 bg-white text-neutral-900 disabled:opacity-50">
                  {busy === 'onboarding' ? 'Working…' : 'Mark onboarding complete →'}
                </button>
              )}
              {!membership.verification_submitted_at && (
                <button type="button" data-testid="founder-mark-verification-btn" disabled={busy === 'verification'} onClick={onMarkVerification}
                        className="px-3 py-2 text-xs uppercase tracking-wider font-mono border border-neutral-900 bg-white text-neutral-900 disabled:opacity-50">
                  {busy === 'verification' ? 'Working…' : 'Mark verification submitted →'}
                </button>
              )}
            </div>
          </Card>
        </div>

        {/* Portal + downgrade controls */}
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Card title="/ Billing" testid="founder-card-billing">
            <p className="text-sm text-neutral-700 leading-relaxed">
              Manage payment methods, download invoices, change billing address, and view subscription detail in the secure Stripe-hosted portal.
            </p>
            <button type="button" data-testid="founder-portal-btn" disabled={busy === 'portal'} onClick={onPortal}
                    className="mt-4 px-4 py-2.5 text-sm uppercase tracking-wider font-mono bg-neutral-900 text-white disabled:opacity-50">
              {busy === 'portal' ? 'Opening Stripe…' : 'Open Stripe portal →'}
            </button>
          </Card>

          <Card title="/ Tier change" testid="founder-card-tier-change">
            {membership.pending_change && membership.pending_change.target_tier ? (
              <div data-testid="founder-pending-downgrade">
                <p className="text-sm text-neutral-700">
                  Scheduled change to <span className="font-mono text-neutral-900">{TIER_META[membership.pending_change.target_tier]?.label || membership.pending_change.target_tier}</span> on {(membership.pending_change.scheduled_for || '').slice(0, 10) || 'renewal'}.
                </p>
                <button type="button" data-testid="founder-cancel-downgrade-btn" disabled={busy === 'cancel-downgrade'} onClick={onCancelDowngrade}
                        className="mt-4 px-4 py-2.5 text-sm uppercase tracking-wider font-mono border border-neutral-900 bg-white text-neutral-900 disabled:opacity-50">
                  {busy === 'cancel-downgrade' ? 'Working…' : 'Cancel scheduled change'}
                </button>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-neutral-700">
                <p>Schedule a downgrade — current benefits continue until renewal.</p>
                <div className="flex flex-wrap gap-2">
                  {STANDARD_TIERS.map(t => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`founder-downgrade-${t}-btn`}
                      onClick={() => setDowngrade({ open: true, target: t, typed: '' })}
                      className="px-3 py-2 text-xs uppercase tracking-wider font-mono border border-neutral-300 bg-white text-neutral-900 hover:border-neutral-900"
                    >
                      Schedule → {TIER_META[t]?.label}
                    </button>
                  ))}
                </div>
                {isFounder && (
                  <p className="mt-3 text-[11px] uppercase tracking-wider text-amber-900 font-mono">
                    Founder → Standard is permanent. You forfeit your founder number and price-lock.
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>

        {/* Downgrade dialog (inline, no Radix dependency) */}
        {downgrade.open && (
          <div data-testid="founder-downgrade-dialog" role="dialog" aria-modal="true"
               className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-md border border-neutral-900 bg-white">
              <div className="border-b border-neutral-900 px-5 py-4">
                <div className="label-overline" style={{ color: GOLD }}>/ Confirm permanent change</div>
                <div className="mt-2 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: '1.5rem' }}>
                  Downgrade to {TIER_META[downgrade.target]?.label}
                </div>
              </div>
              <div className="p-5 text-sm text-neutral-700">
                <p>This permanently forfeits your founder number, founder badge, and 10-year price-lock. It cannot be undone after the effective date.</p>
                <p className="mt-3">Type <span className="font-mono text-neutral-900">DOWNGRADE</span> to confirm.</p>
                <input
                  type="text"
                  data-testid="founder-downgrade-typed-input"
                  value={downgrade.typed}
                  onChange={(e) => setDowngrade(d => ({ ...d, typed: e.target.value }))}
                  className="mt-2 w-full border border-neutral-900 px-3 py-2 font-mono uppercase tracking-wider"
                  placeholder="DOWNGRADE"
                  autoFocus
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" data-testid="founder-downgrade-cancel-btn"
                          onClick={() => setDowngrade({ open: false, target: '', typed: '' })}
                          className="px-3 py-2 text-xs uppercase tracking-wider font-mono border border-neutral-300">
                    Cancel
                  </button>
                  <button type="button" data-testid="founder-downgrade-confirm-btn"
                          disabled={busy === 'downgrade' || downgrade.typed.trim().toUpperCase() !== 'DOWNGRADE'}
                          onClick={onConfirmDowngrade}
                          className="px-3 py-2 text-xs uppercase tracking-wider font-mono bg-neutral-900 text-white disabled:opacity-40">
                    {busy === 'downgrade' ? 'Working…' : 'Confirm downgrade →'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default FounderDashboard;
